import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, FileText, UserRound } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { SermonCard } from "@/components/SermonCard";
import { Overline } from "@/components/motion";
import { useLang } from "@/context/LangContext";
import { PageLayout } from "@/components/PageLayout";

const usePastorsData = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/pastors").catch(() => ({ data: [] })), api.get("/sermons").catch(() => ({ data: [] }))])
      .then(([p, s]) => setD({ pastors: Array.isArray(p.data) ? p.data : [], sermons: Array.isArray(s.data) ? s.data : [] }));
  }, []);
  return d;
};
const same = (a = "", b = "") => a.trim().toLowerCase() === b.trim().toLowerCase();

const Photo = ({ p, className }) => (p.photo_url
  ? <img src={fileUrl(p.photo_url)} alt={p.name} className={`object-cover ${className}`} />
  : <span className={`grid place-items-center bg-[#1C2D42] ${className}`}><UserRound className="h-1/3 w-1/3 text-[#B7BFCC]" /></span>);

export default function Pastors() {
  const { t, pick } = useLang();
  const d = usePastorsData();
  return (
    <main data-testid="pastors-page">
      <PageLayout page="pastors" sections={{
        hero: <PageHero testid="pastors-hero" over={t("pastors.over")} title={t("pastors.title")} lead={t("pastors.lead")} />,
        list: (
      <section className="mx-auto grid max-w-[1400px] gap-6 px-5 pb-24 sm:grid-cols-2 md:px-10 lg:grid-cols-3">
        {d?.pastors.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}>
            <Link to={`/pastors/${p.slug}`} data-testid={`pastor-card-${p.slug}`} className="group block overflow-hidden rounded-[28px] border border-[#F4EEE2]/10 bg-[#131C2E]">
              <div className="relative aspect-[4/5] overflow-hidden"><Photo p={p} className="absolute inset-0 h-full w-full transition-transform duration-700 group-hover:scale-[1.04]" /></div>
              <div className="flex items-end justify-between gap-3 p-6">
                <div className="min-w-0">
                  <p data-ve-rec={`pastors|${p.id}|name`} className="font-display text-3xl font-semibold leading-tight text-[#F4EEE2]">{p.name}</p>
                  {(p.role_en || p.role_mh) && <p data-ve-rec={`pastors|${p.id}|role_`} className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">{pick(p.role_en, p.role_mh)}</p>}
                  {(p.bio_en || p.bio_mh) && <p data-testid={`pastor-card-bio-${p.slug}`} data-ve-rec={`pastors|${p.id}|bio_`} className="mt-3 line-clamp-3 text-sm leading-relaxed text-[#C9D0DB]">{pick(p.bio_en, p.bio_mh)}</p>}
                  <p className="mt-3 text-xs text-[#B7BFCC]">{p.files?.length || 0} {t("pastors.filesCount")} · {d.sermons.filter((s) => same(s.speaker, p.name)).length} {t("pastors.messages")}</p>
                </div>
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#D4AF37] text-[#0B111E] transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
              </div>
            </Link>
          </motion.div>
        ))}
        {d && !d.pastors.length && <p data-testid="pastors-empty" className="py-16 font-display text-3xl italic text-[#B7BFCC] sm:col-span-2 lg:col-span-3">{t("pastors.empty")}</p>}
      </section>
        ),
      }} />
    </main>
  );
}

const fileDay = (f) => f.date || (f.uploaded_at || "").slice(0, 10);
// Newest first: the date the team set, or the day the file was uploaded.
export const sortFiles = (files = []) => [...files].sort((a, b) => fileDay(b).localeCompare(fileDay(a)));
const showDay = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "");

const Files = ({ files }) => {
  const { t } = useLang();
  return (
    <div className="mx-auto max-w-[1400px] px-5 pt-16 md:px-10" data-testid="pastor-files">
      <Overline>{t("pastors.files")}</Overline>
      {!files.length && <p data-testid="pastor-no-files" className="mt-6 text-[#B7BFCC]">{t("pastors.noFiles")}</p>}
      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {files.map((f, i) => (
          <a key={`${f.url}-${i}`} href={fileUrl(f.url)} target="_blank" rel="noopener noreferrer" data-testid={`pastor-file-${i}`} className="group block">
            <div className="relative aspect-[1/1.414] overflow-hidden rounded-[4px] bg-white p-4 shadow-[0_1px_2px_rgba(20,33,61,0.08),0_14px_30px_-14px_rgba(20,33,61,0.35)] transition-transform duration-300 group-hover:-translate-y-1">
              {(f.type || "").startsWith("image/") ? <img src={fileUrl(f.url)} alt="" loading="lazy" className="h-full w-full object-contain object-top" /> : (
                <>
                  <FileText className="h-8 w-8 text-[#B4442A]" />
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#5F6875]">{(f.name || "").split(".").pop()?.slice(0, 5) || "file"}</p>
                  <div className="mt-4 grid gap-1.5">{[90, 100, 80, 95, 70, 85].map((w, j) => <span key={j} style={{ width: `${w}%` }} className="h-1 rounded bg-[#14213D]/10" />)}</div>
                </>
              )}
              <span className="absolute right-0 top-0 h-5 w-5 bg-gradient-to-bl from-[#FBF8F2] from-50% to-[#D9D0BE] to-50%" />
            </div>
            <p data-testid={`pastor-file-title-${i}`} className="mt-2 line-clamp-2 break-words font-ui text-xs font-semibold text-[#F4EEE2]">{f.title || f.name || "File"}</p>
            {fileDay(f) && <p data-testid={`pastor-file-date-${i}`} className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-[#B7BFCC]">{showDay(fileDay(f))}</p>}
          </a>
        ))}
      </div>
    </div>
  );
};

export function PastorPage() {
  const { slug } = useParams();
  const { t, pick } = useLang();
  const d = usePastorsData();
  if (!d) return <main className="min-h-screen" />;
  const p = d.pastors.find((x) => x.slug === slug);
  if (!p) return <Navigate to="/pastors" replace />;
  const msgs = d.sermons.filter((s) => same(s.speaker, p.name));
  const files = sortFiles(p.files);
  return (
    <main data-testid={`pastor-page-${slug}`} className="pb-24 pt-32 md:pt-40">
      <PageLayout page={`pastor:${slug}`} sections={{
        profile: (
      <section className="mx-auto max-w-[1400px] px-5 md:px-10">
      <Link to="/pastors" data-testid="pastor-back-link" className="mb-10 inline-flex items-center gap-2 font-ui text-sm text-[#B7BFCC] hover:text-[#D4AF37]"><ArrowLeft className="h-4 w-4" /> {t("pastors.all")}</Link>
      <div className="grid items-start gap-10 md:grid-cols-12">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="md:col-span-4">
          <Photo p={p} className="aspect-[4/5] w-full rounded-[32px]" />
        </motion.div>
        <div className="md:col-span-8">
          {(p.role_en || p.role_mh) && <Overline><span data-ve-rec={`pastors|${p.id}|role_`}>{pick(p.role_en, p.role_mh)}</span></Overline>}
          <h1 data-testid="pastor-name" data-ve-rec={`pastors|${p.id}|name`} className="font-display mt-4 text-5xl font-semibold leading-[1.02] text-[#F4EEE2] sm:text-6xl lg:text-7xl">{p.name}</h1>
          {(p.bio_en || p.bio_mh) && <p data-testid="pastor-bio" data-ve-rec={`pastors|${p.id}|bio_`} className="mt-6 max-w-2xl whitespace-pre-line text-base leading-relaxed text-[#C9D0DB]">{pick(p.bio_en, p.bio_mh)}</p>}
        </div>
      </div>
      </section>
        ),
        files: <Files files={files} />,
        messages: (
      <div className="mx-auto max-w-[1400px] px-5 pt-20 md:px-10">
        <Overline>{t("pastors.messagesBy")}</Overline>
        {msgs.length ? (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{msgs.map((s, i) => <SermonCard key={s.id} s={s} index={i} />)}</div>
        ) : <p data-testid="pastor-no-messages" className="mt-6 text-[#B7BFCC]">{t("pastors.noMessages")}</p>}
      </div>
        ),
      }} />
    </main>
  );
}
